import React, { createContext, useCallback, useState, useEffect, useContext } from 'react';
import { puree } from '@ux/pure-enhance';
import { useWindow } from '@ux/hooks';
import { defaultProps } from '@team-griffin/rehook';

/* eslint-disable max-len */
// Used as min-width triggers
// Rationale for these numbers:
// https://sketch.cloud/s/4gYaZ/all/rationale/brand-ga-stats
// https://confluence.emea.godaddy.com:8443/pages/viewpage.action?spaceKey=PL&title=GoDaddy+EMEA+Unified+Design+System
// example @ https://www.mattstevens.co.uk/godaddy/grid/
// enums create an object containing both key => val and val => key
// we only need the `key => val`
var breakpoints = {
  xs: 0,
  sm: 750,
  md: 1020,
  lg: 1300,
  xl: 1800
};

var BreakpointContext = createContext(null);
var Provider = BreakpointContext.Provider,
    Consumer = BreakpointContext.Consumer;

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var ucfirst = function ucfirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

var useMatchMedia = function useMatchMedia(breakpoints) {
  var window = useWindow();
  var getValues = useCallback(function () {
    // see which breakpoint queries are true
    return Object.keys(breakpoints).reduce(function (prev, cur) {
      return Object.assign({}, prev, _defineProperty({}, "isMin".concat(ucfirst(cur)), window.matchMedia("(min-width: ".concat(breakpoints[cur], "px)")).matches));
    }, {});
  }, [breakpoints]);

  var _useState = useState(getValues),
      _useState2 = _slicedToArray(_useState, 2),
      values = _useState2[0],
      setValues = _useState2[1];

  useEffect(function () {
    var handler = function handler() {
      return setValues(getValues);
    };

    window.addEventListener('resize', handler);
    return function () {
      return window.removeEventListener('resize', handler);
    };
  }, [breakpoints]);
  return values;
};

var enhance = puree('BreakpointRoot')();

var BreakpointRoot = function BreakpointRoot(_ref) {
  var children = _ref.children;
  var values = useMatchMedia(breakpoints);
  return /*#__PURE__*/React.createElement(Provider, {
    value: values
  }, children);
};

var BreakpointRoot$1 = enhance(BreakpointRoot); // eslint-disable-next-line no-underscore-dangle

var useBreakpoints = function useBreakpoints() {
  return useContext(BreakpointContext);
};

var withBreakpoints = function withBreakpoints(props) {
  return Object.assign({}, useBreakpoints(), props);
};

var withBreakpoints$1 = puree('WithBreakpointsHoc')(withBreakpoints);

var enhance$1 = puree('StubBreakpointRoot')(defaultProps({
  isMinSm: false,
  isMinMd: false,
  isMinLg: false,
  isMinXl: false
}));

var StubBreakpointRoot = function StubBreakpointRoot(_ref) {
  var children = _ref.children,
      isMinSm = _ref.isMinSm,
      isMinMd = _ref.isMinMd,
      isMinLg = _ref.isMinLg,
      isMinXl = _ref.isMinXl;
  return /*#__PURE__*/React.createElement(Provider, {
    value: {
      isMinSm: isMinSm,
      isMinMd: isMinMd,
      isMinLg: isMinLg,
      isMinXl: isMinXl
    }
  }, children);
};

var StubBreakpointRoot$1 = enhance$1(StubBreakpointRoot); // eslint-disable-next-line no-underscore-dangle

export { BreakpointRoot$1 as BreakpointRoot, StubBreakpointRoot$1 as StubBreakpointRoot, breakpoints, useBreakpoints, withBreakpoints$1 as withBreakpoints, withBreakpoints as withBreakpointsHook };
